import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import StrokeText from "@/components/StrokeText"
import Toast from "@/components/Toast"
import { useOwnTeam } from "@/hooks"
import { i18n } from "@/i18n"
import { getTaskStatus } from "@/services/core"
import { useRoleInfo } from "@/user/session"

import styles from "./index.module.scss"

const LOGIN_TASK_CID = "T_FpMemberLogin5_20261008"
const LOGIN_TASK_TOTAL = 5

// ─── 有成员时展示 ─────────────────────────────────────────────

interface MemberInfoProps {
    uid: string
    nickname: string | null
    avatarUrl: string | null
    loginCount: number
}

const MemberInfo: React.FC<MemberInfoProps> = ({ uid, nickname, avatarUrl, loginCount }) => {
    const barRef = useRef<HTMLDivElement>(null)
    const carRef = useRef<HTMLDivElement>(null)

    const percent = useMemo(
        () => Math.min(loginCount / LOGIN_TASK_TOTAL, 1),
        [loginCount],
    )

    useEffect(() => {
        if (barRef.current) {
            barRef.current.style.clipPath = `inset(0 ${(1 - percent) * 100}% 0 0)`
        }
        if (carRef.current) {
            carRef.current.style.left = `${percent * 100}%`
        }
    }, [percent])

    return (
        <div className={styles.memberInfo}>
            <div className={styles.info}>
                <div className={styles.avatar}>
                    {avatarUrl && <img src={avatarUrl} alt={nickname ?? ""} />}
                </div>
                <div className={styles.right}>
                    <div className={styles.uid}>{uid}</div>
                    <div
                        className={styles.intro}
                        dangerouslySetInnerHTML={{
                            __html: i18n.getRecall("progress.text_member", {
                                count: String(loginCount),
                                total: String(LOGIN_TASK_TOTAL),
                            }),
                        }}
                    />
                </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.progressBar}>
                <div className={styles.wrapper}>
                    <div className={styles.bar}>
                        <div ref={barRef} className={styles.barInner} />
                    </div>
                </div>
                <div ref={carRef} className={styles.car} />
            </div>
            <div className={styles.current}>
                <span>{loginCount}</span>/{LOGIN_TASK_TOTAL}
            </div>
            <div className={styles.total}>
                {i18n.getRecall("progress.text_total")}
            </div>
        </div>
    )
}

// ─── 无成员时展示 ─────────────────────────────────────────────

interface EmptyProps {
    unsatisfied?: boolean
    inviteCode: string
    copyFunc: () => Promise<boolean>
}

const Empty: React.FC<EmptyProps> = (props) => {
    const { inviteCode, copyFunc, unsatisfied } = props

    const copy = useCallback(async () => {
        const res = await copyFunc()
        if (res) {
            Toast.message(i18n.getRecall("message.copy_success"))
        } else {
            Toast.message(i18n.getRecall("message.copy_fail"))
        }
    }, [copyFunc])

    return (
        <div className={styles.empty}>
            <div
                className={styles.slogan}
                dangerouslySetInnerHTML={{
                    __html: unsatisfied
                        ? i18n.getRecall("progress.text_unsatisfied")
                        : i18n.getRecall("progress.text_noMember"),
                }}
            />
            {!unsatisfied && (
                <>
                    <div className={styles.code}>
                        <div className={styles.label}>
                            <StrokeText
                                text={i18n.getRecall("progress.text_inviteCode")}
                                fontFamily="a한글세상B"
                                fontSize={34}
                                fillColor="#ffffff"
                                borderWidth={3}
                                borderColor="#614518"
                                offsetY={0}
                                padding={3}
                                baselineAligned
                            />
                        </div>
                        <div className={styles.value}>{inviteCode}</div>
                    </div>
                    <button type="button" className={styles.copy} onClick={copy}>
                        <StrokeText
                            text={i18n.getRecall("progress.text_copy")}
                            fontFamily="a한글세상B"
                            fontSize={37}
                            fillColor="#ffffff"
                            borderWidth={3}
                            borderColor="#564426"
                            offsetY={0}
                            padding={3}
                            baselineAligned
                            className={styles.text}
                        />
                    </button>
                </>
            )}
        </div>
    )
}

// ─── 主组件 ───────────────────────────────────────────────────

export const Progress: React.FC = () => {
    const user = useRoleInfo()
    const { data: ownTeam } = useOwnTeam()
    const [loginCount, setLoginCount] = useState(0)

    useEffect(() => {
        if (!user || !ownTeam?.member) return
        getTaskStatus().then((res) => {
            if (res.code === 0) {
                const item = res.data.find((t) => t.cid === LOGIN_TASK_CID)
                setLoginCount(item?.progress?.value ?? 0)
            }
        })
    }, [user, ownTeam?.member])

    const copyFunc = useCallback(async () => {
        if (!ownTeam?.code) return false
        try {
            await navigator.clipboard.writeText(ownTeam.code)
            return true
        } catch {
            return false
        }
    }, [ownTeam?.code])

    return (
        <div className={styles.progress}>
            {ownTeam?.member ? (
                <MemberInfo
                    uid={ownTeam.member.uid}
                    nickname={ownTeam.member.nickname}
                    avatarUrl={ownTeam.member.avatar?.url ?? null}
                    loginCount={loginCount}
                />
            ) : (
                <Empty
                    inviteCode={ownTeam?.code ?? "-"}
                    copyFunc={copyFunc}
                    unsatisfied={false}
                />
            )}
        </div>
    )
}

export default Progress
